import { useMemo } from "react";

import {
  Badge,
  FontIcon,
  Table,
  TableColumnDefinition,
  TableQuery,
  TableRowDefinition,
} from "@ezmid/components-library";
import {
  faCoins,
  faDatabase,
  faFingerprint,
  faInfoCircle,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useGetBackOfficeListPaymentsQuery } from "../../../api";
import { useFormatters } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useTableQuery } from "../../../hooks/useTableQuery";
import { AllRoutes } from "../../route/Route";

const localStorageKey = "payment.page";

type TableData = {
  userID: string;
  status: string;
  paymentVendor: string;
  priceCurrencyCode: string;
  priceMicros: number;
  vendorProductID: string;
  createdAt: string;
};

export function PaymentsListPageContent<T extends object>() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  const { query, updateQuery } = useTableQuery(
    localStorageKey,
    {
      pageSize: 10,
      pageIndex: 1,
      keyword: "",
      sortBy: { key: "name", order: "desc" },
      columnCategoryFilter: [],
    },
    AllRoutes.PAYMENTS_LIST
  );

  const { currentData, isFetching } = useGetBackOfficeListPaymentsQuery(
    {
      page: query?.pageIndex,
      size: query?.pageSize,
      keyword: query?.keyword,
      sort: [`${query.sortBy?.key}:${query.sortBy?.order}`],
      status: query.columnCategoryFilter,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((payment) => {
        return {
          userID: payment?.user?.id || "",
          status: payment?.status || "",
          paymentVendor: payment?.paymentVendor || "",
          priceCurrencyCode: payment?.priceCurrencyCode || "",
          priceMicros: payment?.priceMicros || 0,
          vendorProductID: payment?.vendorProductID || "",
          createdAt: payment?.createdAt || "",
          id: payment?.id || "",
        };
      }) || [],
    [currentData]
  );
  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.userID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original.userID.toUpperCase().substr(0, 8)}
          </Badge>
        ),
        id: "userID",
        accessor: "userID",
        width: "200px",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const status =
            row?.original.status === "ps-purchased"
              ? "success"
              : row?.original.status === "ps-canceled"
              ? "danger"
              : "warning";

          return (
            <Badge status={status} variant="pill">
              {row?.original.status.split("-")[1]}
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
        width: "200px",
      },
      {
        Header: t("table.header.paymentVendor"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge variant="pill">
            {row?.original.paymentVendor?.split("-")[1] || ""}
          </Badge>
        ),
        id: "paymentVendor",
        accessor: "paymentVendor",
        width: "150px",
      },
      {
        Header: t("table.header.priceCurrencyCode"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faCoins} />}
              />
            }
          >
            {row?.original.priceCurrencyCode ?? ""}
          </Badge>
        ),
        id: "priceCurrencyCode",
        accessor: "priceCurrencyCode",
        width: "200px",
      },
      {
        Header: t("table.header.priceMicros"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faDatabase} />}
              />
            }
          >
            {row?.original.priceMicros ?? ""}
          </Badge>
        ),
        id: "priceMicros",
        accessor: "priceMicros",
        width: "200px",
      },
      {
        Header: t("table.header.vendorProductID"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const badgeLabel = !row?.original?.vendorProductID
            ? t("label.free")
            : row?.original?.vendorProductID;

          return (
            <div className="min-w-0 break-all [&_*]:!whitespace-normal">
              <Badge
                variant="pill"
                leftIcon={
                  <FontIcon
                    marginTop={3}
                    customIcon={
                      <FontAwesomeIcon size="lg" icon={faFingerprint} />
                    }
                  />
                }
              >
                {badgeLabel}
              </Badge>
            </div>
          );
        },
        id: "vendorProductID",
        accessor: "vendorProductID",
        width: "200px",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
        width: "200px",
      },
    ],
    []
  );

  const onFetchData = ({
    pageIndex,
    pageSize,
    keyword: queryKeyword,
    sortBy,
    columnCategoryFilter,
  }: TableQuery) => {
    updateQuery({
      pageIndex,
      pageSize,
      keyword: queryKeyword || "",
      sortBy,
      columnCategoryFilter,
    });
  };
  return (
    <div className="p-2 w-full min-w-full max-h-fit">
      <div>
        <Table
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faList} />}
            />
          }
          headerText={t("card.header.list")}
          tableData={memoizedData}
          tableColumns={columns}
          showPagination
          showToolbar
          onRowSelect={(row) =>
            navigate(
              makeRoute(AllRoutes.PAYMENT_DETAIL, { id: row.original?.id })
            )
          }
          onFetchData={onFetchData}
          totalCountOfRecords={currentData?.count || 0}
          query={{
            pageIndex: query.pageIndex,
            pageSize: query.pageSize,
            keyword: query.keyword,
            sortBy: query.sortBy,
          }}
          isLoading={isFetching}
          shouldAddEmptyRowsOnLastPage={currentData?.last}
          columnTypeFilter={[
            {
              label: t("app.status.payment.canceled"),
              category: "ps-canceled",
              icon: (
                <FontAwesomeIcon
                  style={{ marginTop: "1px" }}
                  icon={faInfoCircle}
                />
              ),
            },
            {
              label: t("app.status.payment.purchased"),
              category: "ps-purchased",
              icon: (
                <FontAwesomeIcon
                  style={{ marginTop: "1px" }}
                  icon={faInfoCircle}
                />
              ),
            },
            {
              label: t("app.status.payment.pending"),
              category: "ps-pending",
              icon: (
                <FontAwesomeIcon
                  style={{ marginTop: "1px" }}
                  icon={faInfoCircle}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
